import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import transform from 'lodash/transform';
import qs from 'qs';
import { discountTypes } from '../constants/subscription/coupon';
import formatCents from '../utils/subscription/formatCents';
import { logClientError } from './logging';
import getDisabledCacheConfig from './utils/getDisabledCacheConfig';
import getCountryCode from './utils/getCountryConfig';

export const GET_PRODUCTS_BY_SKUS = 'GET_PRODUCTS_BY_SKUS';
const skuFetchError = 'SKU FETCH ERROR';

/**
 * applyCouponDiscount - creates formattedDiscountedPrice and discountedCurrencies properties for a
 * product, based on the coupon data available.
 *
 * @param  {type} product    the product metadata
 * @param  {type} couponData the coupon metadata
 * @return {type}            the old product definition, or new one with discount metadata.
 */
export function applyCouponDiscount(product, couponData) {
  // Only apply a discount to a product if either:
  // 1. the coupon itsel applies to all metadata.
  // 2. the product is included in the plans that the coupon applies to.
  if (couponData.appliesToAllPlans || couponData.plans.includes(product.sku)) {
    const newProduct = { ...product };

    // Calculate discounted prices based on percentage.
    if (couponData.type === discountTypes.percent) {
      const percentage = 1 - couponData.percentage / 100;

      if (percentage === 0) {
        newProduct.isFree = true;
      }

      newProduct.formattedDiscountedPrice = formatCents(
        Math.ceil(newProduct.price * percentage),
        newProduct.currencyCode,
      );

      newProduct.discountedCurrencies = transform(
        newProduct.currencies,
        (result, value, key) => {
          // eslint-disable-next-line no-param-reassign
          result[key] = Math.ceil(value * percentage);
        },
        {},
      );
    } else if (couponData.type === discountTypes.dollars) {
      // Calculate discounted prices based on the amount to subtract.
      const discountedCentsForCurrency =
        couponData.centsCurrencyMap[newProduct.currencyCode];
      const newPrice = newProduct.price - discountedCentsForCurrency;

      if (newPrice === 0) {
        newProduct.isFree = true;
      }

      newProduct.formattedDiscountedPrice = discountedCentsForCurrency
        ? formatCents(newPrice, newProduct.currencyCode)
        : newProduct.formattedPrice;

      newProduct.discountedCurrencies = transform(
        newProduct.currencies,
        (result, value, key) => {
          const discountedCents = couponData.centsCurrencyMap[key];

          // eslint-disable-next-line no-param-reassign
          result[key] = discountedCents ? value - discountedCents : value;
        },
        {},
      );
    } else if (couponData.type === discountTypes.freeTrial) {
      newProduct.discountFreeTrialLength = couponData.freeTrialAmount;
      newProduct.discountFreeTrialUnit = couponData.freeTrialUnit;
    }

    return newProduct;
  }

  return product;
}

function transformResponse(getState) {
  return ({ data }) => {
    const couponData = get(getState(), 'subscription.coupon');

    if (couponData && couponData.type) {
      if (isArray(data)) {
        return {
          content: data.map((product) =>
            applyCouponDiscount(product, couponData),
          ),
        };
      }

      return { content: applyCouponDiscount(data, couponData) };
    }

    return { content: data };
  };
}

function getProductsBySkusEffect(skus, countryCode, skuMeta) {
  return (dispatch, getState) => {
    const params = {
      skus: qs.stringify({ skus }, { indices: false }),
      countryCode,
      noCache: getDisabledCacheConfig(getState()),
    };

    return dispatch({
      type: GET_PRODUCTS_BY_SKUS,
      api: {
        endpoint: ['subscription', 'getProductsBySkus'],
        args: [params],
        transform: transformResponse(getState),
      },
      meta: {
        skuMeta,
        contentId: params.skus,
      },
    });
  };
}

/**
 * @param {string[]} skus - array of all the skus
 * @param {Object} meta -
  {
    defaultSkus: ['sku1', 'sku2'],
    fromQuery: ['sku3', 'sku4'],
    isRenew: false,
  }
 * @returns {Function}
 */
export function getProductsBySkus(skus, meta) {
  return (dispatch, getState) => {
    let shouldRequest = false;
    const state = getState();
    const countryCode = getCountryCode(state);

    skus.forEach((sku) => {
      if (isEmpty(getState().products[sku])) {
        shouldRequest = true;
      }
    });

    if (!shouldRequest) {
      return Promise.resolve();
    }

    return dispatch(getProductsBySkusEffect(skus, countryCode, meta)).catch(
      (error) => {
        dispatch(
          logClientError({
            message: `${skuFetchError}: ${error.message}`,
            context: {
              skus,
            },
          }),
        );

        throw error;
      },
    );
  };
}
